<template>
  <div class="book">
    <r-main-panel :title="`${$t('book:title')}`" icon="book">
      <div class="book-tree__container">
        <profile-tree
          :show-tools="true"
          :node-click-handler="beforeOpenBook"
          :active-layers="activeBook"
          :active-layers-id="activeBookId"
          module="bookTree"
        />
      </div>
    </r-main-panel>
    <div class="book-content" v-loading="isLoading">
      <div class="table" v-if="tableReady">
        <bookTable :active-book="activeBook" />
        <div
          v-if="!isFullData && tableReady && isShowButton"
          class="book-content__tooltip"
        >
          <el-button
            @click="increaseLimit"
            class="r-button"
            size="mini"
            type="primary"
          >
            {{ $t("book:load-more") }}
          </el-button>
        </div>
      </div>
      <div v-else class="book-content__welcome">
        <div class="book-content__welcome-icon">
          <r-icon name="ksodd-tables" :size="64" />
        </div>
        <div class="book-content__welcome-text">
          <r-title type="title-2" colorType="subhead">
            {{ $t("book:select-ds") }}
          </r-title>
        </div>
      </div>
    </div>
    <r-info-panel v-if="activeCards.length">
      <active-cards module="book" />
    </r-info-panel>
  </div>
</template>

<script>
import ActiveCards from "@/components/object-info/active-cards";
import profileTree from "@/components/profiles-tree/profiles-tree.vue";
import bookTable from "./book-table/book-table.vue";

import { parseObjectFields, addListenerToSocket, errorParser } from "@/utils";

export default {
  components: {
    ActiveCards,
    profileTree,
    bookTable
  },
  props: ["sourcename"],
  data() {
    return {
      activeBook: "",
      activeBookId: "",
      isFullData: false,
      limit: 1000,

      tableReady: false
    };
  },
  watch: {
    "$store.state.book.activeTable": {
      handler: function(value) {
        if (value.fields.length) this.tableReady = true;
      },
      deep: true,
      immediate: true
    },
    limit: {
      handler: function(value) {
        if (value !== 1000) {
          const object = this.$store.state.book.activeTable.source_id
            ? this.$store.state.book.activeTable
            : { source_id: this.activeBook, id: this.activeBookId };

          this.openBook({}, object, true);
        }
      },
      deep: true,
      immediate: true
    },
    updateTable(val) {
      if (!val) return;
      const object = this.$store.state.book.activeTable.source_id
        ? this.$store.state.book.activeTable
        : { source_id: this.activeBook, id: this.activeBookId };

      this.openBook({}, object, true);
      this.$store.commit("UPDATE_ACTIVE_TABLE", false);
    },
    sortConfigs(val) {
      if (!val) return;
      const object = this.$store.state.book.activeTable.source_id
        ? this.$store.state.book.activeTable
        : { source_id: this.activeBook, id: this.activeBookId };

      this.openBook({}, object, true);
    }
  },
  computed: {
    activeCards() {
      return this.$store.state.commonData.activeCards.book || [];
    },
    filtersConfigs() {
      return this.$store.state.book.filters.filtersConfigs;
    },
    sortConfigs() {
      return this.$store.state.book.sortConfig;
    },
    isLoading() {
      return this.$store.state.book.tableLoading;
    },
    isShowButton() {
      return this.$store.state.book.showButton;
    },
    updateTable() {
      return this.$store.state.book.update;
    }
  },
  methods: {
    setActiveFields(activeFields) {
      const fields = activeFields ? activeFields.fields : null;

      this.$store.commit("SET_ACTIVE_FIELDS", { fields });
    },
    setFiltersConfigs(filtersConfigs) {
      if (filtersConfigs && filtersConfigs.length) {
        filtersConfigs.forEach(e => {
          this.$store.commit("SET_TABLE_FILTER", {
            field: e.field,
            op: e.op,
            type: "AND",
            value: e.value,
            name: e.name,
            id: e.id,
            active: e.active
          });
        });
      }
    },
    async beforeOpenBook(_, object) {
      const { source_id, datatype, id } = object;
      if (!source_id || datatype === "model") return;

      this.$store.commit("CLEAR_TABLE_FILTER");
      this.$store.commit("REMOVE_TABLE_SORT_CONFIG");
      const { data } = await this.$store.dispatch("GET_MODULE_USER_CONFIG", {
        module: "book",
        id
      });
      const activeFields = data.activeFields;
      const filters = data.filters;

      this.setActiveFields(activeFields);
      if (filters) this.setFiltersConfigs(filters.filtersConfigs);
      this.openBook(_, object);
    },
    async openBook(_, object, force = false) {
      const { source_id, datatype, id, name, geomType } = object;
      if (!source_id) return;
      if (!force) {
        if (datatype === "model" || this.activeBookId === id) return;
      }
      this.$store.commit("SHOW_DATA_INCREASE_BUTTON", false);
      this.$store.commit("SET_TABLE_LOADING", true);
      if (this.activeBookId !== id) this.limit = 1000;
      this.activeBook = source_id;
      this.activeBookId = id;

      try {
        let options = {};
        let order;
        let fields;

        order =
          this.sortConfigs && this.sortConfigs.sortOrder
            ? [{ [this.sortConfigs.attr]: this.sortConfigs.sortOrder }]
            : ["id"];

        if (this.$store.state.listeners.indexOf(source_id) === -1) {
          this.$store.commit("ADD_LISTENER_ID", source_id);
          addListenerToSocket.call(this, source_id);
        }
        const initialFields = await this.$store.dispatch("REQUEST", {
          url: `objectFields/${source_id}`
        });
        fields = parseObjectFields(initialFields, false, null, "book");

        if (this.filtersConfigs.length) {
          let where = [];

          this.filtersConfigs.forEach(e => {
            if (e.active) {
              const filterItem = {
                field: e.field,
                op: e.op,
                type: e.type,
                value: e.value
              };

              where.push(filterItem);

              if (e.op === "!null") {
                where.push({
                  field: e.field,
                  op: "!=",
                  type: e.type,
                  value: ""
                });
              }
            }
          });
          options.where = where;
        }

        options.limit = this.limit;
        options.order = order;

        const url = `objectInfo/${source_id}?config=${JSON.stringify(options)}`;
        const { data } = await this.$store.dispatch("REQUEST", { url });
        const metadata = await this.$store.dispatch("REQUEST", {
          url: `aggregation/${source_id}`
        });
        const mappedData = this.mapGeom(data);
        const metadataLength = metadata.data.metadata.count;

        this.isFullData = metadataLength >= this.limit ? false : true;

        this.$store.commit("SET_ACTIVE_TABLE", {
          source_id: this.activeBook,
          full: this.isFullData,
          limit: this.limit,
          dataLength: metadataLength,
          data: mappedData,
          id,
          fields,
          name,
          geomType
        });
      } catch (e) {
        errorParser.call(this, e);
        this.$store.commit("SET_TABLE_LOADING", false);
      }
    },
    mapGeom(data) {
      Object.keys(data).forEach(e => {
        if (data[e].geom) {
          if (data[e].geom.type === "Point") {
            data[e].geom = data[e].geom.coordinates
              ? data[e].geom.coordinates.join(", ")
              : "-";
          } else {
            data[e].geom = data[e].geom.type || "-";
          }
        } else {
          data[e].geom = "-";
        }
      });
      return data;
    },
    increaseLimit() {
      this.limit += 1000;
    }
  },
  created() {
    const activeTable = this.$store.state.book.activeTable;

    this.isFullData =
      Object.keys(activeTable.data).length >= this.limit ? false : true;
    if (this.sourcename) {
      this.beforeOpenBook({}, { source_id: this.sourcename });
    } else if (activeTable.source_id) {
      this.beforeOpenBook({}, activeTable);
    }
  }
};
</script>

<style lang="scss" scoped>
.book {
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
  overflow: hidden;
  .r-main-panel {
    z-index: 105;
  }
  &-tree__container {
    padding: 8px;
    box-sizing: border-box;
    height: 100%;
  }
  .book-content {
    flex: 1;
    height: 100%;
    overflow-y: auto;
    position: relative;
    min-width: 215px;
    &__tooltip {
      position: absolute;
      height: 32px;
      bottom: 12px;
      left: 50%;
      transform: translateX(-50%);
      filter: opacity(0.95);
    }
    &__welcome {
      position: absolute;
      top: 20%;
      left: 50%;
      transform: translate(-50%);
      min-width: 200px;
      max-width: 320px;
      display: flex;
      align-items: center;
      justify-content: center;
      flex-direction: column;
    }
    &__welcome-text {
      font-size: 14px;
      text-align: center;
      margin-top: 16px;
    }
  }
}
</style>

<i18n>
{
  "ru": {
    "book:title": "Справочник",
    "book:load-more": "Загрузить еще",
    "book:select-ds": "Для начала работы выберите источник данных в панели слева"
  },
  "en": {
    "book:title": "Book",
    "book:load-more": "Load more",
    "book:select-ds": "To get started, select a data source in the left pane"
  }
}
</i18n>
